import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { orderApi } from "../api/orderApi";
import { paymentApi } from "../api/paymentApi";
import Message from "../components/Message";

const completedFor = {
  saved: [0],
  // Paid orders have passed checkout and are ready for staff to create a shipment.
  paid: [0],
  pending: [0],
  processing: [0, 1],
  shipped: [0, 1, 2],
  delivered: [0, 1, 2, 3],
};

export default function OrderTracking() {
  const { id } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethodId, setSelectedMethodId] = useState("");
  const [notice, setNotice] = useState(null);
  const [shipment, setShipment] = useState(undefined);

  useEffect(() => {
    if (location.state?.notice) {
      setNotice({ type: "success", text: location.state.notice });
    }
    orderApi.getOrder(id).then((data) => {
      const loadedOrder = data.order || data;
      setOrder(loadedOrder);
      setEditItems((loadedOrder.items || []).map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
      })));
    }).catch((error) => setNotice({ type: "error", text: error.message }));

    orderApi.getOrderShipment(id).then((data) => setShipment(data.shipment)).catch(() => setShipment(null));

    paymentApi.getPaymentMethods().then((data) => {
      // Saved methods are loaded here because payment happens after checkout creates the saved order.
      const methods = data.payment_methods || [];
      setPaymentMethods(methods);
      if (methods[0]) {
        setSelectedMethodId(String(methods[0].id));
      }
    }).catch(() => {});
  }, [id, location.state]);

  async function handleCancel() {
    if (!window.confirm("Are you sure you want to cancel this order?")) return;
    try {
      const data = await orderApi.cancelOrder(order.id);
      setOrder(data.order || order);
      setNotice({ type: "success", text: "Order cancelled successfully." });
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    }
  }

  function updateEditQuantity(productId, quantity) {
    setEditItems((current) => current.map((item) => (
      item.product_id === productId
        ? { ...item, quantity: Number(quantity) }
        : item
    )));
  }

  function removeEditItem(productId) {
    setEditItems((current) => current.map((item) => (
      item.product_id === productId ? { ...item, quantity: 0 } : item
    )));
  }

  async function handleUpdateOrder(event) {
    event.preventDefault();
    try {
      const data = await orderApi.updateOrder(order.id, { items: editItems });
      const updatedOrder = data.order || data;
      setOrder(updatedOrder);
      setEditItems((updatedOrder.items || []).map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
      })));
      setNotice({ type: "success", text: "Order updated successfully." });
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    }
  }

  async function handlePayment(event) {
    event.preventDefault();
    try {
      // Payment uses both an existing saved order and an existing saved method, matching Feature 05.
      const data = await paymentApi.makePayment({
        order_id: order.id,
        payment_method_id: Number(selectedMethodId),
      });
      setOrder((current) => ({ ...current, status: data.order_status || "paid" }));
      setNotice({ type: "success", text: "Payment completed. Order marked as paid." });
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    }
  }

  if (!order) {
    return <div className="container"><Message notice={notice || { type: "info", text: "Loading order..." }} /></div>;
  }

  const status = order.status || "saved";
  const completed = completedFor[status] || [0];
  const isSaved = status === "saved";

  return (
    <div className="tracking-container">
      <div className="container">
        <h1 className="page-title">Order Tracking</h1>
        <Message notice={notice} />

        <div className="tracking-card">
          <div className="order-header">
            <h2>Order #{order.order_number}</h2>
            <span className={`status-badge status-${status}`}>{String(status).toUpperCase()}</span>
          </div>

          <div className="tracking-timeline">
            {[
              ["Order Confirmed", new Date(order.created_at).toLocaleString()],
              ["Processing", "Your order is being prepared"],
              ["Shipped", "Your package is on the way"],
              ["Delivered", status === "delivered"
                ? new Date(order.updated_at || order.estimated_arrival || order.created_at).toLocaleDateString()
                : `Estimated arrival: ${order.estimated_arrival ? new Date(order.estimated_arrival).toLocaleDateString() : "Not yet shipped"}`],
            ].map(([title, text], index) => (
              <div key={title} className={`timeline-item ${completed.includes(index) ? "completed" : ""} ${status === "processing" && index === 1 ? "active" : ""} ${status === "shipped" && index === 2 ? "active" : ""} ${status === "delivered" && index === 3 ? "active" : ""}`}>
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <h4>{title}</h4>
                  <p>{text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="order-details">
            <h3>Order Details</h3>
            <div className="details-grid">
              <div className="detail-item"><span className="detail-label">Order Date:</span><span className="detail-value">{new Date(order.created_at).toLocaleDateString()}</span></div>
              <div className="detail-item"><span className="detail-label">Total Amount:</span><span className="detail-value">${Number(order.total_price).toFixed(2)}</span></div>
              <div className="detail-item"><span className="detail-label">Delivery Address:</span><span className="detail-value">{order.delivery_address}</span></div>
              <div className="detail-item"><span className="detail-label">Status:</span><span className="detail-value">{String(status).toUpperCase()}</span></div>
            </div>

            <h3 style={{ marginTop: 30 }}>Items</h3>
            <form onSubmit={handleUpdateOrder}>
              <table className="items-table">
                <thead>
                  <tr><th>Product</th><th>Quantity</th><th>Price</th><th>Subtotal</th>{isSaved && <th>Action</th>}</tr>
                </thead>
                <tbody>
                  {(order.items || []).map((item, index) => {
                    const editableItem = editItems.find((editItem) => editItem.product_id === item.product_id);
                    const displayQuantity = editableItem ? editableItem.quantity : item.quantity;
                    return (
                      <tr key={index}>
                        <td>{item.product_name || item.product?.name}</td>
                        <td>
                          {isSaved ? (
                            <input
                              type="number"
                              min="0"
                              className="quantity-input"
                              value={displayQuantity}
                              onChange={(event) => updateEditQuantity(item.product_id, event.target.value)}
                            />
                          ) : item.quantity}
                        </td>
                        <td>${Number(item.price).toFixed(2)}</td>
                        <td>${(Number(item.price) * Number(displayQuantity || 0)).toFixed(2)}</td>
                        {isSaved && (
                          <td>
                            <button type="button" className="btn btn-sm btn-danger" onClick={() => removeEditItem(item.product_id)}>
                              Remove
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {isSaved && (
                <button type="submit" className="btn btn-primary" style={{ marginTop: 16 }}>
                  Update Order
                </button>
              )}
            </form>
          </div>

          <div style={{ marginTop: 20, display: "flex", gap: 12 }}>
            <Link to="/my-orders" className="btn btn-secondary">Back to Orders</Link>
            {isSaved && (
              <button onClick={handleCancel} className="btn btn-danger">Cancel Order</button>
            )}
          </div>

          {shipment !== undefined && (
            <div className="order-summary" style={{ marginTop: 24 }}>
              <h3>Shipment</h3>
              {shipment === null ? (
                <p style={{ color: "#666" }}>
                  {status === "paid"
                    ? "Your order has been paid. A shipment will be created by our staff shortly."
                    : "No shipment yet."}
                </p>
              ) : (
                <div className="details-grid">
                  <div className="detail-item"><span className="detail-label">Shipment ID:</span><span className="detail-value">#{shipment.id}</span></div>
                  <div className="detail-item"><span className="detail-label">Delivery Address:</span><span className="detail-value">{shipment.address}</span></div>
                  <div className="detail-item"><span className="detail-label">Status:</span><span className="detail-value" style={{ textTransform: "capitalize" }}>{shipment.status}</span></div>
                  <div className="detail-item"><span className="detail-label">Created:</span><span className="detail-value">{new Date(shipment.created_at).toLocaleString()}</span></div>
                </div>
              )}
            </div>
          )}

          {isSaved && (
            <div className="order-summary" style={{ marginTop: 24 }}>
              <h3>Pay This Order</h3>
              {paymentMethods.length > 0 ? (
                <form onSubmit={handlePayment}>
                  <div className="form-group">
                    <label htmlFor="payment_method_id">Saved Payment Method</label>
                    <select
                      id="payment_method_id"
                      className="form-control"
                      value={selectedMethodId}
                      onChange={(event) => setSelectedMethodId(event.target.value)}
                      required
                    >
                      {paymentMethods.map((method) => (
                        <option key={method.id} value={method.id}>
                          {method.payment_type === "credit_card"
                            ? `${method.brand || "Card"} ending ${method.last_four}`
                            : `${method.bank_name} ending ${method.account_last_four}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button type="submit" className="btn btn-primary">Pay Order</button>
                </form>
              ) : (
                <p>
                  Add a saved payment method before paying this order.{" "}
                  <Link to="/payment-methods">Manage payment methods</Link>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
